import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import DVHLineChart from '~/components/Charts/DVHLineChart';
import PanelHeader from '~/components/toolbar/shared/PanelHeader';
import {
    DVH_METRICS,
    dvhMetricDifferenceGy,
    DvhMetric,
    summarizeDvh,
} from '~/domain/dose/dvhComparison';
import { useDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { replanningCompareViewState } from '~/state/atoms/adaptiveReplanningAtoms';
import { dicomColorToRgbString } from '~/utils/color';
import { dvhComparisonCurves } from './dvhComparisonCurves';
import { useDvhComparison } from './useDvhComparison';

export const DVH_COMPARISON_PANEL_WIDTH = 600;
const DRAGGABLE_ID = 'replanning-dvh-comparison';
const DEFAULT_SELECTION = 4;

const METRIC_LABEL_ID: Record<DvhMetric, string> = {
    meanGy: 'replanning.dvh.metric.mean',
    maxGy: 'replanning.dvh.metric.max',
    d95Gy: 'replanning.dvh.metric.d95',
    d2Gy: 'replanning.dvh.metric.d2',
};

export interface DvhPanelPosition {
    readonly x: number;
    readonly y: number;
}

export default function DvhComparisonContent({
    position,
    onClose,
}: {
    position: DvhPanelPosition;
    onClose: () => void;
}) {
    const intl = useIntl();
    const setComparing = useSetRecoilState(replanningCompareViewState);
    const [selected, setSelected] = useState<readonly string[]>([]);
    const {
        pairing,
        availability,
        entries,
        loading,
        failed,
        priorDose,
        currentDose,
    } = useDvhComparison(selected, true);
    const { settings } = useDoseDisplaySettings(priorDose?.id ?? null);
    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useDraggable({ id: DRAGGABLE_ID });
    const focusRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        focusRef.current?.focus();
    }, []);

    const pairNames = useMemo(
        () => pairing.pairs.map((pair) => pair.name),
        [pairing]
    );
    useEffect(() => {
        setSelected((current) => {
            const kept = current.filter((name) => pairNames.includes(name));
            return kept.length > 0
                ? kept
                : pairNames.slice(0, DEFAULT_SELECTION);
        });
    }, [pairNames]);

    const toggle = (name: string) =>
        setSelected((current) =>
            current.includes(name)
                ? current.filter((candidate) => candidate !== name)
                : [...current, name]
        );

    // Curves only from two doses: a missing grid maximum is never stood in for.
    const curves = useMemo(
        () =>
            priorDose && currentDose
                ? dvhComparisonCurves(
                      entries,
                      {
                          priorMaxGy: priorDose.doseMaxGy,
                          currentMaxGy: currentDose.doseMaxGy,
                      },
                      {
                          prior: intl.formatMessage({
                              id: 'replanning.dvh.prior',
                          }),
                          current: intl.formatMessage({
                              id: 'replanning.dvh.current',
                          }),
                      }
                  )
                : [],
        [entries, priorDose, currentDose, intl]
    );
    const gy = (value: number | undefined) =>
        value === undefined
            ? '–'
            : intl.formatNumber(value, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              });
    const delta = (value: number | undefined) =>
        value === undefined ? '–' : `${value > 0 ? '+' : ''}${gy(value)}`;
    // Ready implies a prior dose; rechecked so the chart is never normalised against a missing one.
    const shownAvailability =
        availability === 'ready' && !priorDose ? 'noPriorDose' : availability;

    return (
        <Paper
            ref={(node) => {
                setNodeRef(node);
                focusRef.current = node;
            }}
            {...attributes}
            tabIndex={-1}
            role="dialog"
            aria-label={intl.formatMessage({ id: 'replanning.dvh.title' })}
            elevation={8}
            data-testid="replanning-dvh-comparison"
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    onClose();
                }
            }}
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                width: DVH_COMPARISON_PANEL_WIDTH,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
                zIndex: 1400,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundImage: 'none',
                outline: 'none',
                transform: isDragging
                    ? CSS.Translate.toString(transform)
                    : undefined,
            }}
        >
            <Stack spacing={1.25} p={1.5}>
                <PanelHeader
                    title={intl.formatMessage({ id: 'replanning.dvh.title' })}
                    dragListeners={listeners}
                    onClose={onClose}
                />
                <Typography fontSize={11} color="text.secondary">
                    <FormattedMessage id="replanning.dvh.explanation" />
                </Typography>
                {shownAvailability !== 'ready' || !priorDose ? (
                    <Alert
                        severity="info"
                        variant="outlined"
                        data-testid={`replanning-dvh-${shownAvailability}`}
                        action={
                            shownAvailability === 'needsComparison' ? (
                                <Button
                                    size="small"
                                    onClick={() => setComparing(true)}
                                    sx={{ fontSize: 11 }}
                                >
                                    <FormattedMessage id="replanning.review.compareReference" />
                                </Button>
                            ) : undefined
                        }
                        sx={{ fontSize: 11.5 }}
                    >
                        <FormattedMessage
                            id={`replanning.dvh.unavailable.${shownAvailability}`}
                        />
                    </Alert>
                ) : (
                    <>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {pairing.pairs.map((pair) => (
                                <Chip
                                    key={pair.name}
                                    size="small"
                                    label={pair.name}
                                    clickable
                                    color={
                                        selected.includes(pair.name)
                                            ? 'primary'
                                            : 'default'
                                    }
                                    variant={
                                        selected.includes(pair.name)
                                            ? 'filled'
                                            : 'outlined'
                                    }
                                    onClick={() => toggle(pair.name)}
                                    aria-pressed={selected.includes(pair.name)}
                                    data-testid="replanning-dvh-structure"
                                    sx={{ fontSize: 11 }}
                                />
                            ))}
                        </Box>
                        {(pairing.onlyPrior.length > 0 ||
                            pairing.onlyCurrent.length > 0) && (
                            <Typography
                                fontSize={11}
                                color="text.secondary"
                                data-testid="replanning-dvh-unpaired"
                            >
                                <FormattedMessage
                                    id="replanning.dvh.unpaired"
                                    values={{
                                        names: [
                                            ...pairing.onlyPrior,
                                            ...pairing.onlyCurrent,
                                        ].join(', '),
                                    }}
                                />
                            </Typography>
                        )}
                        {failed && (
                            <Alert
                                severity="error"
                                variant="outlined"
                                sx={{ fontSize: 11.5 }}
                            >
                                <FormattedMessage id="replanning.dvh.failed" />
                            </Alert>
                        )}
                        <Box height={260} position="relative">
                            <DVHLineChart
                                data={curves}
                                normalizationValueGy={
                                    settings.normalizationValueGy ??
                                    priorDose.doseMaxGy
                                }
                                loading={loading}
                            />
                        </Box>
                        {loading && entries.length === 0 && (
                            <Box display="flex" justifyContent="center">
                                <CircularProgress size={16} />
                            </Box>
                        )}
                        {entries.length > 0 && (
                            <Table
                                size="small"
                                data-testid="replanning-dvh-table"
                                sx={{
                                    '& td, & th': {
                                        fontSize: 11,
                                        px: 0.75,
                                        py: 0.25,
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <FormattedMessage id="replanning.dvh.structure" />
                                        </TableCell>
                                        {DVH_METRICS.map((metric) => (
                                            <TableCell
                                                key={metric}
                                                align="right"
                                            >
                                                <FormattedMessage
                                                    id={METRIC_LABEL_ID[metric]}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entries.map((entry) => {
                                        const prior = entry.prior
                                            ? summarizeDvh(entry.prior)
                                            : undefined;
                                        const current = entry.current
                                            ? summarizeDvh(entry.current)
                                            : undefined;
                                        return (
                                            <TableRow key={entry.name}>
                                                <TableCell>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={0.5}
                                                    >
                                                        <Box
                                                            width={8}
                                                            height={8}
                                                            borderRadius="50%"
                                                            bgcolor={dicomColorToRgbString(
                                                                entry.color
                                                            )}
                                                        />
                                                        {entry.name}
                                                    </Box>
                                                </TableCell>
                                                {DVH_METRICS.map((metric) => (
                                                    <TableCell
                                                        key={metric}
                                                        align="right"
                                                    >
                                                        <Box>
                                                            {gy(
                                                                current?.[
                                                                    metric
                                                                ]
                                                            )}
                                                        </Box>
                                                        <Box color="text.secondary">
                                                            {gy(
                                                                prior?.[metric]
                                                            )}
                                                        </Box>
                                                        <Box fontWeight={500}>
                                                            {prior && current
                                                                ? delta(
                                                                      dvhMetricDifferenceGy(
                                                                          prior,
                                                                          current,
                                                                          metric
                                                                      )
                                                                  )
                                                                : '–'}
                                                        </Box>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                        {entries.length > 0 && (
                            <Typography fontSize={10.5} color="text.secondary">
                                <FormattedMessage id="replanning.dvh.tableLegend" />
                            </Typography>
                        )}
                    </>
                )}
            </Stack>
        </Paper>
    );
}
